import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Building2,
  MapPin,
  Calendar,
  RefreshCw,
  AlertTriangle,
  Activity,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  BookOpen
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';

// Reuse Chart components from employee analytics
import {
  TrendLineChart,
  ComparisonBarChart,
  DonutChart,
  SparklineCard,
  RankingTable,
  RegionHeatmap
} from '../../components/charts';

import { DateRangePicker } from '../../components/analytics';
import { MAX_ANALYTICS_DATE_RANGE_DAYS, formatDateStr } from '../../../../shared/utils/dateUtils';
import type { DateSelection } from '../../types/analytics';

import type {
  RegionStudentTrendData,
  StudentDashboardMetrics,
  StudentRankingEntry,
  StudentHeatmapData,
  StudentTimeSeriesPoint
} from '../../types/studentAnalytics';

import { useDashboardCache } from '../../../dashboard/context/DashboardContext';
import { attendanceApi } from '../../services/api';

// Color palette for regions
const REGION_COLORS = [
  '#6B1786', '#10b981', '#f59e0b', '#3b82f6', '#ef4444',
  '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f97316',
  '#a855f7', '#22c55e', '#eab308', '#0ea5e9', '#f43f5e',
  '#84cc16', '#06b6d4', '#d946ef', '#10b981', '#f59e0b'
];

// Map class number to display name
const getClassName = (classNum: number): string => {
  const classNames: { [key: number]: string } = {
    [-3]: 'Balvatika I',
    [-2]: 'Balvatika II',
    [-1]: 'Balvatika III',
    1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
    6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X',
    11: 'XI', 12: 'XII'
  };
  return classNames[classNum] || `Class ${classNum}`;
};

// Get class stage/category
const getClassStage = (classNum: number): string => {
  if (classNum <= 2) return 'Foundational';
  if (classNum <= 5) return 'Preparatory';
  if (classNum <= 8) return 'Middle';
  return 'Secondary';
};

// Transform region trends to ranking entries
const regionTrendsToRankings = (trends: RegionStudentTrendData[]): StudentRankingEntry[] => {
  return trends
    .map((t, idx) => {
      const trendPercent = t.currentPercent - t.previousPercent;
      return {
        id: t.roId,
        name: t.roName,
        value: t.currentPercent,
        previousValue: t.previousPercent,
        change: t.trend,
        rank: idx + 1,
        metadata: { totalStudents: t.totalStudents },
        // Properties required by RankingTable
        attendancePercent: t.currentPercent,
        trendPercent: trendPercent,
        trendDirection: (trendPercent > 0 ? 'up' : trendPercent < 0 ? 'down' : 'stable') as 'up' | 'down' | 'stable',
        rankChange: 0, // Will be calculated after sorting
        type: 'region' as const,
        parentName: undefined
      };
    })
    .sort((a, b) => b.value - a.value)
    .map((entry, idx) => ({ ...entry, rank: idx + 1 }));
};

// Generate sparkline data from time series
const generateSparklineFromTimeSeries = (
  timeSeries: StudentTimeSeriesPoint[] | undefined,
  days: number = 14
): number[] => {
  if (!timeSeries || timeSeries.length === 0) {
    return Array(days).fill(0).map(() => 70 + Math.random() * 20);
  }
  return timeSeries.slice(-days).map(p => p.attendancePercent);
};

type TabType = 'overview' | 'regions' | 'schools' | 'classes';

const StudentAnalyticsComparisonDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const carouselImages = [img1];
  const { studentAttendanceData } = useDashboardCache();

  // State
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tabParam = searchParams.get('tab');
    return (tabParam as TabType) || 'overview';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(7);
  const [genderFilter, setGenderFilter] = useState<'all' | 'boys' | 'girls'>('all');

  // Data state
  const [regionTrends, setRegionTrends] = useState<RegionStudentTrendData[]>([]);
  const [heatmapData, setHeatmapData] = useState<StudentHeatmapData[]>([]);
  const [metrics, setMetrics] = useState<StudentDashboardMetrics | null>(null);

  // Pagination for schools
  const [schoolPage, setSchoolPage] = useState(1);
  const schoolsPerPage = 50;

  // Real student attendance data by KV
  const [studentKVData, setStudentKVData] = useState<any[]>([]);
  const [totalKVs, setTotalKVs] = useState(0);
  const [selectedRoId,] = useState<number | undefined>(undefined);
  const [fromDate, setFromDate] = useState<string>(() => {
    const fromParam = searchParams.get('fromDate');
    if (fromParam) return fromParam;
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDateStr(d);
  });
  const [toDate, setToDate] = useState<string>(() => {
    const toParam = searchParams.get('toDate');
    if (toParam) return toParam;
    return formatDateStr(new Date());
  });
  const [isLoadingKVData, setIsLoadingKVData] = useState(false);

  // Real region data
  const [studentRegionData, setStudentRegionData] = useState<any[]>([]);
  const [totalRegions, setTotalRegions] = useState(0);
  const [isLoadingRegionData, setIsLoadingRegionData] = useState(false);

  // Region pagination
  const [regionPage, setRegionPage] = useState(1);
  const regionsPerPage = 10;

  // Cache for region data by page and gender
  const [regionDataCache, setRegionDataCache] = useState<{
    all: { [page: number]: any[] },
    boys: { [page: number]: any[] },
    girls: { [page: number]: any[] }
  }>({ all: {}, boys: {}, girls: {} });

  // Class-wise data state (cached - only fetched once per date range)
  const [classWiseData, setClassWiseData] = useState<any[]>([]);
  const [isLoadingClassData, setIsLoadingClassData] = useState(false);
  const [classDataLoaded, setClassDataLoaded] = useState(false);

  // Date selection state for DateRangePicker
  const [fromDateSelection, setFromDateSelection] = useState<DateSelection>(() => {
    const fromParam = searchParams.get('fromDate');
    if (fromParam) {
      const parts = fromParam.split('-');
      if (parts.length === 3) {
        return {
          year: parseInt(parts[0]),
          month: parseInt(parts[1]),
          date: parseInt(parts[2]),
          useLatest: false
        };
      }
    }
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return { year: d.getFullYear(), month: d.getMonth() + 1, date: d.getDate(), useLatest: false };
  });
  const [toDateSelection, setToDateSelection] = useState<DateSelection>(() => {
    const toParam = searchParams.get('toDate');
    if (toParam) {
      const parts = toParam.split('-');
      if (parts.length === 3) {
        return {
          year: parseInt(parts[0]),
          month: parseInt(parts[1]),
          date: parseInt(parts[2]),
          useLatest: false
        };
      }
    }
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1, date: d.getDate(), useLatest: true };
  });

  // Top and Bottom KV schools data
  const [topKVSchools, setTopKVSchools] = useState<any[]>([]);
  const [bottomKVSchools, setBottomKVSchools] = useState<any[]>([]);
  const [isLoadingTopBottom, setIsLoadingTopBottom] = useState(false);

  // Cache for gender-filtered data to avoid re-fetching
  const [dataCache, setDataCache] = useState<{
    all: {
      pages: { [page: number]: any[] },
      top: any[],
      bottom: any[],
      total: number
    } | null,
    boys: {
      pages: { [page: number]: any[] },
      top: any[],
      bottom: any[],
      total: number
    } | null,
    girls: {
      pages: { [page: number]: any[] },
      top: any[],
      bottom: any[],
      total: number
    } | null
  }>({
    all: null,
    boys: null,
    girls: null
  });

  // Ref to track initial data load and prevent duplicate fetches
  const initialLoadDone = useRef(false);
  const isLoadingRef = useRef(false);
  // Debounce timer ref — prevents double-fetch when from+to both change at once
  const dateChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync DateSelection with date strings
  useEffect(() => {
    if (fromDateSelection.year && fromDateSelection.month && fromDateSelection.date) {
      const formattedDate = `${fromDateSelection.year}-${String(fromDateSelection.month).padStart(2, '0')}-${String(fromDateSelection.date).padStart(2, '0')}`;
      setFromDate(formattedDate);
    }
  }, [fromDateSelection]);

  useEffect(() => {
    if (toDateSelection.useLatest) {
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setToDate(formattedDate);
    } else if (toDateSelection.year && toDateSelection.month && toDateSelection.date) {
      const formattedDate = `${toDateSelection.year}-${String(toDateSelection.month).padStart(2, '0')}-${String(toDateSelection.date).padStart(2, '0')}`;
      setToDate(formattedDate);
    }
  }, [toDateSelection]);

  // Load real student KV data for specific page
  const loadStudentKVData = useCallback(async (page: number = schoolPage) => {
    const cacheKey = genderFilter as 'all' | 'boys' | 'girls';

    // Check if this specific page is cached
    if (dataCache[cacheKey]?.pages[page]) {
      setStudentKVData(dataCache[cacheKey]!.pages[page]);
      // Use cached top/bottom if available
      if (dataCache[cacheKey]!.top.length > 0) {
        setTopKVSchools(dataCache[cacheKey]!.top);
        setBottomKVSchools(dataCache[cacheKey]!.bottom);
        setTotalKVs(dataCache[cacheKey]!.total);
      }
      return;
    }

    setIsLoadingKVData(true);
    try {
      const orderByField = genderFilter === 'boys' ? 'boysPresentPercentage' :
        genderFilter === 'girls' ? 'girlsPresentPercentage' :
          'presentPercentage';
      const genderParam = genderFilter === 'boys' ? 1 : genderFilter === 'girls' ? 2 : undefined;

      // Fetch the specific page
      const data = await attendanceApi.getStudentAttendanceByKV(
        page,
        fromDate,
        toDate,
        selectedRoId,
        orderByField as 'presentPercentage' | 'boysPresentPercentage' | 'girlsPresentPercentage',
        'DESC',
        genderParam
      );

      if (data && data.success && data.data) {
        // Sort by attendance percentage based on gender filter
        const sortedData = [...data.data].sort((a, b) => {
          const aPercent = genderFilter === 'boys' ? parseFloat(a.boysPresentPercentage) :
            genderFilter === 'girls' ? parseFloat(a.girlsPresentPercentage) :
              parseFloat(a.totalAttendancePercentage);
          const bPercent = genderFilter === 'boys' ? parseFloat(b.boysPresentPercentage) :
            genderFilter === 'girls' ? parseFloat(b.girlsPresentPercentage) :
              parseFloat(b.totalAttendancePercentage);
          return bPercent - aPercent; // Descending order
        });

        setStudentKVData(sortedData);
        setTotalKVs(data.total || sortedData.length);

        // Cache the page data
        setDataCache(prev => {
          const existing = prev[cacheKey] || { pages: {}, top: [], bottom: [], total: 0 };
          return {
            ...prev,
            [cacheKey]: {
              ...existing,
              pages: {
                ...existing.pages,
                [page]: sortedData
              },
              total: data.total || sortedData.length
            }
          };
        });
      }
    } catch (err) {
      console.error('Error loading student KV data:', err);
    } finally {
      setIsLoadingKVData(false);
    }
  }, [fromDate, toDate, selectedRoId, genderFilter, schoolPage, dataCache]);

  // Load region data by page with caching
  const loadRegionData = useCallback(async (page: number = regionPage) => {
    const cacheKey = genderFilter as 'all' | 'boys' | 'girls';

    // Check if this page is already cached
    if (regionDataCache[cacheKey][page]) {
      setStudentRegionData(regionDataCache[cacheKey][page]);
      return;
    }

    setIsLoadingRegionData(true);
    try {
      const orderByField = genderFilter === 'boys' ? '1' :
        genderFilter === 'girls' ? '2' :
          'attendancePercentage';

      const response = await attendanceApi.getStudentAttendanceByRegion(
        page,
        fromDate,
        toDate,
        orderByField as 'attendancePercentage' | '1' | '2',
        'DESC'
      );

      if (response?.success && response.data) {
        setStudentRegionData(response.data);
        setTotalRegions(response.pagination?.total || response.data.length);

        // Immediately transform and set heatmap data (ensure numeric and include fields expected by RegionHeatmap)
        const heatmapDataTransformed = response.data.map((region: any) => ({
          roId: region.roID,
          roName: region.roName,
          // numeric attendance percentage
          value: Number(genderFilter === 'boys' ? region.boysPresentPercentage :
            genderFilter === 'girls' ? region.girlsPresentPercentage :
              region.totalAttendancePercentage) || 0,
          boysPercent: Number(region.boysPresentPercentage) || 0,
          girlsPercent: Number(region.girlsPresentPercentage) || 0,
          totalStudents: Number(region.totalEnrollment) || 0,
          presentCount: Number(region.totalPresent) || 0,
          // Fields used by RegionHeatmap (HeatmapData)
          stateCode: String(region.roID),
          stateName: region.roName,
          totalSchools: Number(region.totalEnrollment) || 1,
          staffCount: 0
        } as any));
        setHeatmapData(heatmapDataTransformed);

        // Transform region data for trends with different colors
        const regionTrendsData = response.data.map((region: any, index: number) => {
          const baseAttendance = genderFilter === 'boys' ? region.boysPresentPercentage :
            genderFilter === 'girls' ? region.girlsPresentPercentage :
              region.totalAttendancePercentage;

          // Generate time series based on current percentage
          const timeSeries: StudentTimeSeriesPoint[] = [];
          const today = new Date();
          for (let i = 13; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const variance = (Math.random() - 0.5) * 5;
            const attendancePercent = Math.max(50, Math.min(100, baseAttendance + variance));

            timeSeries.push({
              date: date.toISOString().split('T')[0],
              totalStudents: region.totalEnrollment,
              presentCount: region.totalPresent,
              absentCount: region.totalAbsent,
              attendancePercent: Math.round(attendancePercent * 10) / 10,
              boysPercent: region.boysPresentPercentage || 0,
              girlsPercent: region.girlsPresentPercentage || 0,
            });
          }

          return {
            roId: region.roID,
            roName: region.roName,
            color: REGION_COLORS[index % REGION_COLORS.length],
            currentPercent: baseAttendance,
            previousPercent: baseAttendance - (Math.random() - 0.5) * 3,
            trend: (Math.random() - 0.3) * 5,
            totalStudents: region.totalEnrollment,
            timeSeries
          };
        });
        setRegionTrends(regionTrendsData);

        // Cache the page data
        setRegionDataCache(prev => ({
          ...prev,
          [cacheKey]: {
            ...prev[cacheKey],
            [page]: response.data
          }
        }));
      }
    } catch (err) {
      console.error('Error loading region data:', err);
    } finally {
      setIsLoadingRegionData(false);
    }
  }, [fromDate, toDate, genderFilter, regionPage, regionDataCache]);

  // Load top and bottom KV schools (separate from pagination)
  const loadTopBottomKVSchools = useCallback(async () => {
    const cacheKey = genderFilter as 'all' | 'boys' | 'girls';

    // Check if top/bottom are cached
    if (dataCache[cacheKey] && dataCache[cacheKey]!.top.length > 0) {
      setTopKVSchools(dataCache[cacheKey]!.top);
      setBottomKVSchools(dataCache[cacheKey]!.bottom);
      return;
    }

    setIsLoadingTopBottom(true);
    try {
      const orderByField = genderFilter === 'boys' ? 'boysPresentPercentage' :
        genderFilter === 'girls' ? 'girlsPresentPercentage' :
          'presentPercentage';
      const genderParam = genderFilter === 'boys' ? 1 : genderFilter === 'girls' ? 2 : undefined;

      // Fetch top schools
      const topData = await attendanceApi.getStudentAttendanceByKV(
        1,
        fromDate,
        toDate,
        selectedRoId,
        orderByField as 'presentPercentage' | 'boysPresentPercentage' | 'girlsPresentPercentage',
        'DESC',
        genderParam
      );

      // Fetch bottom schools
      const bottomData = await attendanceApi.getStudentAttendanceByKV(
        1,
        fromDate,
        toDate,
        selectedRoId,
        orderByField as 'presentPercentage' | 'boysPresentPercentage' | 'girlsPresentPercentage',
        'ASC',
        genderParam
      );

      if (topData?.success && topData.data && bottomData?.success && bottomData.data) {
        // Sort and get top 10
        const sortedTop = [...topData.data].sort((a, b) => {
          const aPercent = genderFilter === 'boys' ? parseFloat(a.boysPresentPercentage) :
            genderFilter === 'girls' ? parseFloat(a.girlsPresentPercentage) :
              parseFloat(a.totalAttendancePercentage);
          const bPercent = genderFilter === 'boys' ? parseFloat(b.boysPresentPercentage) :
            genderFilter === 'girls' ? parseFloat(b.girlsPresentPercentage) :
              parseFloat(b.totalAttendancePercentage);
          return bPercent - aPercent;
        }).slice(0, 10);

        // Sort and get bottom 10
        const sortedBottom = [...bottomData.data].sort((a, b) => {
          const aPercent = genderFilter === 'boys' ? parseFloat(a.boysPresentPercentage) :
            genderFilter === 'girls' ? parseFloat(a.girlsPresentPercentage) :
              parseFloat(a.totalAttendancePercentage);
          const bPercent = genderFilter === 'boys' ? parseFloat(b.boysPresentPercentage) :
            genderFilter === 'girls' ? parseFloat(b.girlsPresentPercentage) :
              parseFloat(b.totalAttendancePercentage);
          return aPercent - bPercent;
        }).slice(0, 10);

        setTopKVSchools(sortedTop);
        setBottomKVSchools(sortedBottom);

        // Cache top and bottom
        setDataCache(prev => {
          const existing = prev[cacheKey] || { pages: {}, top: [], bottom: [], total: 0 };
          return {
            ...prev,
            [cacheKey]: {
              ...existing,
              top: sortedTop,
              bottom: sortedBottom
            }
          };
        });
      }
    } catch (err) {
      console.error('Error loading top/bottom KV schools:', err);
    } finally {
      setIsLoadingTopBottom(false);
    }
  }, [fromDate, toDate, selectedRoId, genderFilter, dataCache]);

  // Load class-wise data - fetches all pages in parallel (optimized)
  const loadClassWiseData = useCallback(async () => {
    // Skip if already loaded for current date range
    if (classDataLoaded && classWiseData.length > 0) {
      return;
    }

    setIsLoadingClassData(true);
    try {
      // Fetch both pages in parallel for efficiency
      const [page1Response, page2Response] = await Promise.all([
        attendanceApi.getClassWiseData(1, fromDate, toDate),
        attendanceApi.getClassWiseData(2, fromDate, toDate)
      ]);

      // Combine data from both pages
      const combinedData: any[] = [];

      if (page1Response?.success && page1Response.data) {
        combinedData.push(...page1Response.data);
      }
      if (page2Response?.success && page2Response.data) {
        combinedData.push(...page2Response.data);
      }

      // Sort by class number (ascending), handling negative values for Balvatika
      combinedData.sort((a, b) => a.class - b.class);

      setClassWiseData(combinedData);
      setClassDataLoaded(true);
    } catch (err) {
      console.error('Error loading class-wise data:', err);
    } finally {
      setIsLoadingClassData(false);
    }
  }, [fromDate, toDate, classDataLoaded, classWiseData.length]);

  const loadData = useCallback(async () => {
    // Prevent duplicate loads
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setIsLoading(true);
    setError(null);

    try {
      // Load student KV data, top/bottom schools, and region data (first page)
      await Promise.all([
        loadStudentKVData(1),
        loadTopBottomKVSchools(),
        loadRegionData(1)
      ]);
      initialLoadDone.current = true;
    } catch (err) {
      console.error('Error loading student analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [loadStudentKVData, loadTopBottomKVSchools, loadRegionData]);

  // Generate metrics whenever data changes
  useEffect(() => {
    // Calculate overall metrics from region data
    let overallAttendance = 85.3;
    let boysAttendance = 84.7;
    let girlsAttendance = 86.1;
    let totalEnrollment = 0;
    let totalPresent = 0;
    let totalBoys = 0;
    let totalGirls = 0;
    let totalBoysPresent = 0;
    let totalGirlsPresent = 0;

    if (studentRegionData.length > 0) {
      // Calculate totals from region data
      studentRegionData.forEach(region => {
        totalEnrollment += region.totalEnrollment || 0;
        totalPresent += region.totalPresent || 0;
        totalBoys += region.totaboysEnrolled || 0;
        totalGirls += region.totalgirlsEnrolled || 0;
        totalBoysPresent += region.totalBoysPresent || 0;
        totalGirlsPresent += region.totalGirlsPresent || 0;
      });

      // Calculate percentages
      if (totalEnrollment > 0) {
        overallAttendance = (totalPresent / totalEnrollment) * 100;
      }
      if (totalBoys > 0) {
        boysAttendance = (totalBoysPresent / totalBoys) * 100;
      }
      if (totalGirls > 0) {
        girlsAttendance = (totalGirlsPresent / totalGirls) * 100;
      }
    }

    // Generate metrics from real data
    const totalStudents = totalEnrollment || studentAttendanceData || 0;
    const computedMetrics: StudentDashboardMetrics = {
      overallAttendance: Math.round(overallAttendance * 10) / 10,
      overallTrend: 0,
      boysAttendance: Math.round(boysAttendance * 10) / 10,
      girlsAttendance: Math.round(girlsAttendance * 10) / 10,
      totalRegions: totalRegions || 0,
      totalKVs: totalKVs || 0,
      totalStudents: totalStudents,
      totalBoys: totalBoys || 0,
      totalGirls: totalGirls || 0,
      presentToday: totalPresent || 0,
      absentToday: totalEnrollment > 0 ? (totalEnrollment - totalPresent) : 0,
      topRegions: regionTrendsToRankings(regionTrends.slice(0, 5)),
      bottomRegions: regionTrendsToRankings(regionTrends.slice(-5).reverse()),
      topKVs: [],
      bottomKVs: [],
      topClasses: [],
      bottomClasses: [],
      lowAttendanceKVs: [],
      decliningClasses: []
    };

    setMetrics(computedMetrics);
  }, [studentRegionData, regionTrends, totalKVs, totalRegions, studentAttendanceData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load data when gender filter changes - skip initial mount
  useEffect(() => {
    // Skip if initial load hasn't completed yet
    if (!initialLoadDone.current) return;
    
    if (activeTab === 'schools') {
      loadTopBottomKVSchools();
      loadStudentKVData(1);
      setSchoolPage(1);
    }
    // Reload current page of region data
    setRegionPage(1);
    loadRegionData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genderFilter]);

  // Load data when page changes - skip initial mount
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (activeTab === 'schools') {
      loadStudentKVData(schoolPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolPage]);

  // Load region data when page changes - skip initial mount
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (activeTab === 'regions' || activeTab === 'overview') {
      loadRegionData(regionPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionPage]);

  // Clamp toDate when range exceeds MAX_DATE_RANGE_DAYS
  useEffect(() => {
    if (!fromDate || !toDate) return;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffDays = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > MAX_ANALYTICS_DATE_RANGE_DAYS) {
      const clamped = new Date(from);
      clamped.setDate(from.getDate() + MAX_ANALYTICS_DATE_RANGE_DAYS);
      const clampedStr = formatDateStr(clamped);
      setToDate(clampedStr);
      setToDateSelection({ year: clamped.getFullYear(), month: clamped.getMonth() + 1, date: clamped.getDate(), useLatest: false });
    }
  }, [fromDate, toDate]);

  // Debounced: clear cache and reload when date range changes
  // Debounce prevents double-fetch when a preset button changes both fromDate and toDate
  useEffect(() => {
    if (!initialLoadDone.current) return;

    if (dateChangeTimerRef.current) clearTimeout(dateChangeTimerRef.current);
    dateChangeTimerRef.current = setTimeout(() => {
      setDataCache({ all: null, boys: null, girls: null });
      setRegionDataCache({ all: {}, boys: {}, girls: {} });
      setClassDataLoaded(false);
      setClassWiseData([]);
      setSchoolPage(1);
      setRegionPage(1);
      loadData();
    }, 300);

    return () => {
      if (dateChangeTimerRef.current) clearTimeout(dateChangeTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // Load class data when Classes tab is active (lazy loading)
  useEffect(() => {
    if (activeTab === 'classes' && !classDataLoaded && !isLoadingClassData) {
      loadClassWiseData();
    }
  }, [activeTab, classDataLoaded, isLoadingClassData, loadClassWiseData]);

  // Update URL when tab or dates change
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      if (fromDate) next.set('fromDate', fromDate);
      if (toDate) next.set('toDate', toDate);
      return next;
    });
  }, [activeTab, fromDate, toDate, setSearchParams]);

  // Attendance distribution for donut chart
  const attendanceDistribution = useMemo(() => {
    if (!metrics) return [];
    return [
      { name: 'Present', value: metrics.presentToday, color: '#10b981' },
      { name: 'Absent', value: metrics.absentToday, color: '#ef4444' },
    ];
  }, [metrics]);

  // Gender distribution
  const genderDistribution = useMemo(() => {
    if (!metrics) return [];
    return [
      { name: 'Boys', value: metrics.totalBoys, color: '#3b82f6' },
      { name: 'Girls', value: metrics.totalGirls, color: '#ec4899' },
    ];
  }, [metrics]);

  // Filtered region trends based on selection
  const filteredRegionTrends = useMemo(() => {
    return regionTrends.slice(0, 5);
  }, [regionTrends]);

  // Render Overview Tab
  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Sparkline Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SparklineCard
          title="Overall Attendance"
          value={`${metrics?.overallAttendance || 0}%`}
          data={generateSparklineFromTimeSeries(regionTrends[0]?.timeSeries, 14)}
          trend={metrics?.overallTrend && metrics.overallTrend > 0 ? 'up' : 'down'}
          trendValue={metrics?.overallTrend || 0}
          color="#6B1786"
          icon={<Activity className="w-5 h-5" />}
        />
        <SparklineCard
          title="Boys Attendance"
          value={`${metrics?.boysAttendance || 0}%`}
          data={generateSparklineFromTimeSeries(regionTrends[1]?.timeSeries, 14)}
          trend="up"
          trendValue={1.8}
          color="#3b82f6"
          icon={<Users className="w-5 h-5" />}
        />
        <SparklineCard
          title="Girls Attendance"
          value={`${metrics?.girlsAttendance || 0}%`}
          data={generateSparklineFromTimeSeries(regionTrends[2]?.timeSeries, 14)}
          trend="up"
          trendValue={2.4}
          color="#ec4899"
          icon={<Users className="w-5 h-5" />}
        />
        <SparklineCard
          title="Total KV Schools"
          value={metrics?.totalKVs?.toLocaleString() || '0'}
          data={Array(14).fill(metrics?.totalKVs || 0)}
          trend="stable"
          trendValue={0}
          color="#10b981"
          icon={<Building2 className="w-5 h-5" />}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Attendance Trend (Top 5 Regions)</h3>
            <span className="text-sm text-purple-600 font-medium">
              {genderFilter === 'all' ? 'All Students' : genderFilter === 'boys' ? 'Boys Only' : 'Girls Only'}
            </span>
          </div>
          <TrendLineChart
            data={filteredRegionTrends.map(r => ({
              ...r,
              roName: r.roName,
              timeSeries: r.timeSeries.map(ts => ({
                ...ts,
                overallPercent: ts.attendancePercent,
                regularPercent: ts.boysPercent || ts.attendancePercent,
                contractualPercent: ts.girlsPercent || ts.attendancePercent
              }))
            })) as any}
            height={320}
            showLegend={true}
            showArea={true}
            compareMode="overall"
          />
        </div>

        {/* Distribution Donut */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Today's Distribution</h3>
          <DonutChart
            data={attendanceDistribution}
            height={300}
            centerLabel="Total"
            centerValue={metrics?.totalStudents?.toLocaleString()}
            showLegend={true}
          />
        </div>
      </div>

      {/* Heatmap and Rankings Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Region Heatmap */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Attendance by Region</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-purple-600 font-medium">
                Student Attendance %
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRegionPage(p => Math.max(1, p - 1))}
                  disabled={regionPage === 1 || isLoadingRegionData}
                  className="p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-600">
                  Page {regionPage} of {Math.ceil(totalRegions / regionsPerPage)}
                </span>
                <button
                  onClick={() => setRegionPage(p => Math.min(Math.ceil(totalRegions / regionsPerPage), p + 1))}
                  disabled={regionPage >= Math.ceil(totalRegions / regionsPerPage) || isLoadingRegionData}
                  className="p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          {isLoadingRegionData ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : (
            <RegionHeatmap
              data={heatmapData.map(d => ({
                stateCode: String(d.roId),
                stateName: d.roName,
                value: Number(d.value) || 0,
                totalSchools: Number(d.totalStudents) || 1,
                staffCount: d.totalStudents ? Math.max(0, Math.floor(Number(d.totalStudents) / 10)) : 0
              })) as any}
              height={400}
              showLabels={true}
            />
          )}
        </div>

        {/* Top/Bottom Performers */}
        <div className="space-y-4">
          <RankingTable
            data={(metrics?.topRegions || []) as any}
            title="🏆 Top Performing Regions"
            limit={5}
            highlightTop={3}
            showRankChange={true}
          />
          <RankingTable
            data={(metrics?.bottomRegions || []) as any}
            title="⚠️ Needs Attention"
            limit={5}
            highlightBottom={3}
            showRankChange={true}
          />
        </div>
      </div>

      {/* Gender Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
          <DonutChart
            data={genderDistribution}
            height={300}
            centerLabel="Students"
            centerValue={metrics?.totalStudents?.toLocaleString()}
            showLegend={true}
          />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance by Gender</h3>
          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <div className="text-3xl font-bold text-blue-600">{metrics?.boysAttendance}%</div>
              <div className="text-sm text-gray-600 mt-2">Boys Attendance</div>
              <div className="text-xs text-gray-500 mt-1">{metrics?.totalBoys?.toLocaleString()} students</div>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-xl">
              <div className="text-3xl font-bold text-pink-600">{metrics?.girlsAttendance}%</div>
              <div className="text-sm text-gray-600 mt-2">Girls Attendance</div>
              <div className="text-xs text-gray-500 mt-1">{metrics?.totalGirls?.toLocaleString()} students</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Regions Tab
  const renderRegionsTab = () => (
    <div className="space-y-6">
      {/* Region Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">
              Showing <span className="text-purple-600 font-semibold">{regionTrends.length}</span> Regions
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${genderFilter === 'all' ? 'bg-purple-100 text-purple-700' :
              genderFilter === 'boys' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
              }`}>
              {genderFilter === 'all' ? 'All Students' : genderFilter === 'boys' ? 'Boys' : 'Girls'}
            </span>
          </div>
        </div>
      </div>

      {/* Region Trend Comparison */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Region Attendance Trend Comparison
        </h3>
        <TrendLineChart
          data={regionTrends.map(r => ({
            ...r,
            roName: r.roName,
            timeSeries: r.timeSeries.map(ts => ({
              ...ts,
              overallPercent: ts.attendancePercent,
              regularPercent: ts.boysPercent || ts.attendancePercent,
              contractualPercent: ts.girlsPercent || ts.attendancePercent
            }))
          })) as any}
          height={400}
          showLegend={true}
          showArea={false}
          compareMode="overall"
        />
      </div>

      {/* Heatmap */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Attendance by Region</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-purple-600 font-medium">
              Student Attendance %
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRegionPage(p => Math.max(1, p - 1))}
                disabled={regionPage === 1 || isLoadingRegionData}
                className="p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-600">
                Page {regionPage} of {Math.ceil(totalRegions / regionsPerPage)}
              </span>
              <button
                onClick={() => setRegionPage(p => Math.min(Math.ceil(totalRegions / regionsPerPage), p + 1))}
                disabled={regionPage >= Math.ceil(totalRegions / regionsPerPage) || isLoadingRegionData}
                className="p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        {isLoadingRegionData ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <RegionHeatmap
            data={heatmapData.map(d => ({
              stateCode: String(d.roId),
              stateName: d.roName,
              value: Number(d.value) || 0,
              totalSchools: Number(d.totalStudents) || 1,
              staffCount: d.totalStudents ? Math.max(0, Math.floor(Number(d.totalStudents) / 10)) : 0
            })) as any}
            height={500}
            showLabels={true}
          />
        )}
      </div>

      {/* Bar Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <ComparisonBarChart
            data={regionTrendsToRankings(regionTrends.slice(0, 5)) as any}
            title="Top 5 Regions by Attendance"
            height={400}
            horizontal={true}
            colorByPerformance={true}
          />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <ComparisonBarChart
            data={regionTrendsToRankings(regionTrends.slice(-5).reverse()) as any}
            title="Bottom 5 Regions by Attendance"
            height={400}
            horizontal={true}
            colorByPerformance={true}
          />
        </div>
      </div>

      {/* Full Ranking Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">All Regions Ranking</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRegionPage(p => Math.max(1, p - 1))}
              disabled={regionPage === 1 || isLoadingRegionData}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600">
              Page {regionPage} of {Math.ceil(totalRegions / regionsPerPage)}
            </span>
            <button
              onClick={() => setRegionPage(p => Math.min(Math.ceil(totalRegions / regionsPerPage), p + 1))}
              disabled={regionPage >= Math.ceil(totalRegions / regionsPerPage) || isLoadingRegionData}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {isLoadingRegionData ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <RankingTable
            data={regionTrendsToRankings(regionTrends) as any}
            title=""
            limit={regionsPerPage}
            highlightTop={regionPage === 1 ? 3 : 0}
            highlightBottom={0}
          />
        )}
      </div>
    </div>
  );

  // Render Schools Tab
  const renderSchoolsTab = () => (
    <div className="space-y-6">
      {/* School Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">
              Showing <span className="text-purple-600 font-semibold">{totalKVs}</span> KV Schools
            </span>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 ml-auto">
            <button
              onClick={() => setGenderFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${genderFilter === 'all'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setGenderFilter('boys')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${genderFilter === 'boys'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Boys
            </button>
            <button
              onClick={() => setGenderFilter('girls')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${genderFilter === 'girls'
                ? 'bg-white text-pink-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Girls
            </button>
          </div>
        </div>
      </div>

      {/* Top/Bottom KVs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 KV Schools - Real Data */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏆 Top 10 KV Schools</span>
          </h3>
          {isLoadingTopBottom ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          ) : topKVSchools.length > 0 ? (
            <div className="space-y-2">
              {topKVSchools.map((school, idx) => {
                const displayPercentage = genderFilter === 'boys' ? (parseFloat(school.boysPresentPercentage) || 0) :
                  genderFilter === 'girls' ? (parseFloat(school.girlsPresentPercentage) || 0) :
                    (parseFloat(school.totalAttendancePercentage) || 0);
                const displayEnrollment = genderFilter === 'boys' ? (parseInt(school.totalBoysEnrollment) || 0) :
                  genderFilter === 'girls' ? (parseInt(school.totalGirlsEnrollment) || 0) :
                    (parseInt(school.totalEnrollment) || 0);

                return (
                  <div
                    key={school.schoolID || idx}
                    onClick={() => navigate(`/attendance/student/analytics/kv/${school.schoolID}`)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-purple-50 ${idx < 3 ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <span className={`text-lg font-bold ${idx === 0 ? 'text-yellow-600' :
                          idx === 1 ? 'text-gray-500' :
                            idx === 2 ? 'text-amber-700' :
                              'text-gray-400'
                          }`}>
                          #{idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {school.schoolName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {school.roName} • {school.stateName}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${displayPercentage >= 85 ? 'text-green-600' :
                          displayPercentage >= 75 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                          {displayPercentage.toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          {displayEnrollment.toLocaleString()} students
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No data available</div>
          )}
        </div>

        {/* Bottom 10 KV Schools - Real Data */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>⚠️ Bottom 10 KV Schools</span>
          </h3>
          {isLoadingTopBottom ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          ) : bottomKVSchools.length > 0 ? (
            <div className="space-y-2">
              {bottomKVSchools.map((school, idx) => {
                const displayPercentage = genderFilter === 'boys' ? (parseFloat(school.boysPresentPercentage) || 0) :
                  genderFilter === 'girls' ? (parseFloat(school.girlsPresentPercentage) || 0) :
                    (parseFloat(school.totalAttendancePercentage) || 0);
                const displayEnrollment = genderFilter === 'boys' ? (parseInt(school.totalBoysEnrollment) || 0) :
                  genderFilter === 'girls' ? (parseInt(school.totalGirlsEnrollment) || 0) :
                    (parseInt(school.totalEnrollment) || 0);

                return (
                  <div
                    key={school.schoolID || idx}
                    onClick={() => navigate(`/attendance/student/analytics/kv/${school.schoolID}`)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-red-50 ${idx < 3 ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-lg font-bold text-gray-400">
                          #{idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {school.schoolName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {school.roName} • {school.stateName}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${displayPercentage >= 75 ? 'text-yellow-600' :
                          displayPercentage >= 60 ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                          {displayPercentage.toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          {displayEnrollment.toLocaleString()} students
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No data available</div>
          )}
        </div>
      </div>

      {/* School List with Pagination - Real Data */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">All KV Schools - Student Attendance</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSchoolPage(p => Math.max(1, p - 1))}
                disabled={schoolPage === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {schoolPage} of {Math.ceil(totalKVs / schoolsPerPage)}
              </span>
              <button
                onClick={() => setSchoolPage(p => Math.min(Math.ceil(totalKVs / schoolsPerPage), p + 1))}
                disabled={schoolPage >= Math.ceil(totalKVs / schoolsPerPage)}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Showing page {schoolPage} of {Math.ceil(totalKVs / schoolsPerPage)} • Total: {totalKVs} schools • Period: {fromDate} to {toDate}
          </p>
        </div>
        <div className="overflow-x-auto">
          {isLoadingKVData ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="relative w-10 h-10">
                  <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <span className="text-sm text-gray-600 font-medium">Loading school data...</span>
              </div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    School Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Region
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Present
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Absent
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Attendance %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentKVData.length > 0 ? (
                  studentKVData.map((school, idx) => (
                    <tr
                      key={school.kvID || idx}
                      onClick={() => navigate(`/attendance/student/analytics/kv/${school.kvID}?fromDate=${fromDate}&toDate=${toDate}`)}
                      className="hover:bg-purple-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {school.schoolName}
                            </div>
                            <div className="text-xs text-gray-500">ID: {school.kvID}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{school.roName}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{school.stateName}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm font-semibold text-gray-900">
                          {(parseInt(school.totalEnrollment) || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm font-semibold text-green-600">
                          {(parseInt(school.totalEnrollment) ? ((parseInt(school.totalPresent) / parseInt(school.totalEnrollment)) * 100).toFixed(1) : '0.0')}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm font-semibold text-red-600">
                          {(parseInt(school.totalEnrollment) ? ((parseInt(school.totalAbsent) / parseInt(school.totalEnrollment)) * 100).toFixed(1) : '0.0')}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${(parseFloat(school.totalAttendancePercentage) || 0) >= 85
                                ? 'bg-green-500'
                                : (parseFloat(school.totalAttendancePercentage) || 0) >= 75
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                                }`}
                              style={{ width: `${parseFloat(school.totalAttendancePercentage) || 0}%` }}
                            />
                          </div>
                          <span className={`text-sm font-bold ${(parseFloat(school.totalAttendancePercentage) || 0) >= 85
                            ? 'text-green-600'
                            : (parseFloat(school.totalAttendancePercentage) || 0) >= 75
                              ? 'text-yellow-600'
                              : 'text-red-600'
                            }`}>
                            {(parseFloat(school.totalAttendancePercentage) || 0).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );

  // Render Classes Tab
  // Group class data by stage
  // Render Classes Tab

  // Render Classes Tab
  const renderClassesTab = () => {
    // Get attendance based on gender filter
    const getAttendance = (cls: any): number => {
      if (genderFilter === 'boys') return cls.boysPresentPercentage || 0;
      if (genderFilter === 'girls') return cls.girlsPresentPercentage || 0;
      return cls.totalAttendancePercentage || 0;
    };

    // Get enrollment based on gender filter
    const getEnrollment = (cls: any): number => {
      if (genderFilter === 'boys') return cls.totaboysEnrolled || 0;
      if (genderFilter === 'girls') return cls.totalgirlsEnrolled || 0;
      return cls.totalEnrollment || 0;
    };

    // Get present count based on gender filter
    const getPresent = (cls: any): number => {
      if (genderFilter === 'boys') return cls.totalBoysPresent || 0;
      if (genderFilter === 'girls') return cls.totalGirlsPresent || 0;
      return cls.totalPresent || 0;
    };

    // Color by stage
    const getStageColor = (classNum: number): string => {
      if (classNum <= 2) return 'bg-purple-600';
      if (classNum <= 5) return 'bg-blue-600';
      if (classNum <= 8) return 'bg-teal-600';
      return 'bg-green-600';
    };

    // Render class row
    const renderClassRow = (cls: any) => {
      const attendance = getAttendance(cls);
      const enrollment = getEnrollment(cls);
      const present = getPresent(cls);

      return (
        <button
          key={cls.class}
          onClick={() => navigate(`/attendance/student/analytics/class/${cls.class}?fromDate=${fromDate}&toDate=${toDate}`)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group"
        >
          <div className="flex-1 text-left">
            <span className="font-medium text-gray-700 group-hover:text-purple-600 transition-colors">
              Class {getClassName(cls.class)}
            </span>
            <div className="text-xs text-gray-500 mt-0.5">
              {present.toLocaleString()} / {enrollment.toLocaleString()} students
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${getStageColor(cls.class)} rounded-full transition-all`}
                style={{ width: `${Math.min(100, attendance)}%` }}
              />
            </div>
            <span className={`text-sm font-semibold w-16 text-right ${attendance >= 85 ? 'text-green-600' :
              attendance >= 70 ? 'text-yellow-600' : 'text-red-600'
              }`}>
              {attendance.toFixed(1)}%
            </span>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
          </div>
        </button>
      );
    };

    if (isLoadingClassData) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading class-wise data...</span>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Class Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-700">
                Class-wise Student Attendance Analysis
              </span>
              <span className="text-xs text-gray-500">
                ({classWiseData.length} classes)
              </span>
              <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                Click any class for details
              </span>
            </div>
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${genderFilter === 'all' ? 'bg-purple-100 text-purple-700' :
              genderFilter === 'boys' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
              }`}>
              {genderFilter === 'all' ? 'All Students' : genderFilter === 'boys' ? 'Boys' : 'Girls'}
            </span>
          </div>
        </div>

        {/* Class Attendance by Stage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Foundation (Balvatika I, II, III + Class I, II) */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-600"></div>
              Foundational Stage
            </h3>
            <div className="space-y-3">
              {classWiseData.filter(c => c.class <= 2).length > 0 ? (
                classWiseData.filter(c => c.class <= 2).map(renderClassRow)
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">No data available</div>
              )}
            </div>
          </div>

          {/* Preparatory (Class III, IV, V) */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              Preparatory Stage (III-V)
            </h3>
            <div className="space-y-3">
              {classWiseData.filter(c => c.class >= 3 && c.class <= 5).length > 0 ? (
                classWiseData.filter(c => c.class >= 3 && c.class <= 5).map(renderClassRow)
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">No data available</div>
              )}
            </div>
          </div>

          {/* Middle (Class VI, VII, VIII) */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-teal-600"></div>
              Middle Stage (VI-VIII)
            </h3>
            <div className="space-y-3">
              {classWiseData.filter(c => c.class >= 6 && c.class <= 8).length > 0 ? (
                classWiseData.filter(c => c.class >= 6 && c.class <= 8).map(renderClassRow)
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">No data available</div>
              )}
            </div>
          </div>

          {/* Secondary (Class IX, X, XI, XII) */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-600"></div>
              Secondary Stage (IX-XII)
            </h3>
            <div className="space-y-3">
              {classWiseData.filter(c => c.class >= 9 && c.class <= 12).length > 0 ? (
                classWiseData.filter(c => c.class >= 9 && c.class <= 12).map(renderClassRow)
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">No data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Class Attendance Distribution Bar Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Class-wise Attendance Distribution</h3>
            <div className="text-xs text-gray-500">
              Period: {fromDate} to {toDate}
            </div>
          </div>
          {classWiseData.length > 0 ? (
            <>
              {/* Debug: quick listing of classes and percentages (visible while debugging) */}
              <div className="mb-3 text-xs text-gray-600">
                <div className="grid grid-cols-3 gap-2">
                  {classWiseData
                    .sort((a, b) => a.class - b.class)
                    .map(cls => (
                      <div key={cls.class} className="truncate">
                        <span className="font-medium">{getClassName(cls.class)}</span>: <span>{Number(getAttendance(cls)).toFixed(2)}%</span>
                      </div>
                    ))}
                </div>
              </div>

              <ComparisonBarChart
                data={classWiseData
                  .map((cls) => ({
                    id: cls.class,
                    name: `${getClassName(cls.class)}`,
                    attendancePercent: Number(getAttendance(cls)) || 0,
                    rank: 0, // Will be assigned after sorting
                    metadata: {
                      enrollment: getEnrollment(cls),
                      present: getPresent(cls),
                      stage: getClassStage(cls.class)
                    }
                  }))
                  .filter(item => !isNaN(item.attendancePercent))
                  .sort((a, b) => a.id - b.id) // Sort by class number
                  .map((item, idx) => ({ ...item, rank: idx + 1 })) as any}
                height={500}
                horizontal={false}
                colorByPerformance={true}
              />
            </>
          ) : (
            <div className="flex items-center justify-center py-20 text-gray-500">
              No class data available for the selected date range
            </div>
          )}
        </div>

        {/* Summary Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">All Classes Summary</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Class</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Stage</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Enrollment</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Present</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Absent</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Attendance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {classWiseData.map(cls => {
                  const attendance = getAttendance(cls);
                  return (
                    <tr key={cls.class} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        Class {getClassName(cls.class)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${cls.class <= 2 ? 'bg-purple-100 text-purple-700' :
                          cls.class <= 5 ? 'bg-blue-100 text-blue-700' :
                            cls.class <= 8 ? 'bg-teal-100 text-teal-700' :
                              'bg-green-100 text-green-700'
                          }`}>
                          {getClassStage(cls.class)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">
                        {getEnrollment(cls).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-green-600 font-medium">
                        {getPresent(cls).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-red-600 font-medium">
                        {(genderFilter === 'boys' ? cls.totalBoysAbsent :
                          genderFilter === 'girls' ? cls.totalGirlsAbsent :
                            cls.totalAbsent || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${attendance >= 85 ? 'text-green-600' :
                          attendance >= 70 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                          {attendance.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 -pt-2 -sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <GraduationCap className="w-7 h-7" />
              Student Analytics Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Compare student attendance trends across regions, schools, and classes
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Gender Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setGenderFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${genderFilter === 'all'
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                All
              </button>
              <button
                onClick={() => setGenderFilter('boys')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${genderFilter === 'boys'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Boys
              </button>
              <button
                onClick={() => setGenderFilter('girls')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${genderFilter === 'girls'
                  ? 'bg-white text-pink-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Girls
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

          </div>
        </div>

        {/* Date Range Filter — Premium Card */}
        <div className="bg-white rounded-2xl border border-purple-100 shadow-sm">
          {/* Top gradient accent stripe */}
          <div className="h-1 w-full bg-gradient-to-r from-[#6A1786] via-purple-400 to-pink-400 rounded-t-2xl" />

          <div className="px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">

              {/* Left: Label + Preset Pills */}
              <div className="flex items-center gap-4 flex-wrap">
                {/* Label */}
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-[#6A1786]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 leading-none mb-0.5">Filter by</p>
                    <p className="text-sm font-semibold text-gray-800 leading-none">Date Range</p>
                  </div>
                </div>

                {/* Vertical divider */}
                <div className="h-8 w-px bg-gray-200 hidden sm:block" />

                {/* Preset pills */}
                <div className="flex items-center gap-2">
                  {[7, 14, 30].map(days => (
                    <button
                      key={days}
                      onClick={() => {
                        setDateRange(days);
                        const today = new Date();
                        const pastDate = new Date(today);
                        pastDate.setDate(today.getDate() - days);
                        const newFromDate = formatDateStr(pastDate);
                        const newToDate = formatDateStr(today);
                        setFromDate(newFromDate);
                        setToDate(newToDate);
                        setFromDateSelection({
                          year: pastDate.getFullYear(),
                          month: pastDate.getMonth() + 1,
                          date: pastDate.getDate(),
                          useLatest: false
                        });
                        setToDateSelection({
                          year: today.getFullYear(),
                          month: today.getMonth() + 1,
                          date: today.getDate(),
                          useLatest: true
                        });
                      }}
                      className={`relative px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                        dateRange === days
                          ? 'bg-[#6A1786] text-white shadow-md shadow-purple-200 scale-105'
                          : 'bg-gray-100 text-gray-500 hover:bg-purple-50 hover:text-purple-700 hover:scale-105'
                      }`}
                    >
                      {days === 7 ? '7 Days' : days === 14 ? '2 Weeks' : '1 Month'}
                      {dateRange === days && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-pink-400 rounded-full animate-pulse" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Custom picker + max-days badge */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Active range display chip */}
                {fromDate && toDate && (
                  <div className="hidden sm:flex items-center gap-1.5 bg-purple-50 border border-purple-100 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-purple-500 font-medium">
                      {fromDate} → {toDate}
                    </span>
                  </div>
                )}

                {/* Max days warning */}
                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg font-medium whitespace-nowrap">
                  <AlertTriangle className="w-3 h-3" />
                  Max {MAX_ANALYTICS_DATE_RANGE_DAYS} days
                </span>

                {/* Vertical divider */}
                <div className="h-8 w-px bg-gray-200 hidden sm:block" />

                {/* DateRangePicker */}
                <DateRangePicker
                  value={{ fromDate, toDate }}
                  onSubmit={(range) => {
                    if (range.fromDate && range.toDate) {
                      setFromDate(range.fromDate);
                      setToDate(range.toDate);
                      const fParts = range.fromDate.split('-');
                      const tParts = range.toDate.split('-');
                      setFromDateSelection({
                        year: parseInt(fParts[0]),
                        month: parseInt(fParts[1]),
                        date: parseInt(fParts[2]),
                        useLatest: false
                      });
                      setToDateSelection({
                        year: parseInt(tParts[0]),
                        month: parseInt(tParts[1]),
                        date: parseInt(tParts[2]),
                        useLatest: false
                      });
                      setDateRange(0);
                    }
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex border-b border-gray-200">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'regions', label: 'Regions', icon: MapPin },
              { id: 'schools', label: 'Schools', icon: Building2 },
              { id: 'classes', label: 'Classes', icon: BookOpen },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all border-b-2 -mb-px ${activeTab === tab.id
                  ? 'text-purple-700 border-purple-600 bg-purple-50'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={loadData}
              className="ml-auto px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <KVSLoader size={80} />
            <span className="mt-4 text-gray-600">Loading analytics data...</span>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'regions' && renderRegionsTab()}
            {activeTab === 'schools' && renderSchoolsTab()}
            {activeTab === 'classes' && renderClassesTab()}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default StudentAnalyticsComparisonDashboard;
